import React, { useEffect, useState } from 'react'
import { Dialog, Button, TextField, Text, Flex } from '@radix-ui/themes'

export function UrlInputDialog({
  open,
  onOpenChange,
  title,
  description,
  placeholder,
  validate,
  errorMessage,
  onSubmit,
  showTextField,
  defaultText,
  defaultUrl,
  textLabel = 'Text to display',
  textPlaceholder = '',
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: string
  description: string
  placeholder: string
  validate: (url: string) => boolean
  errorMessage: string
  onSubmit: (url: string, text?: string) => void
  showTextField?: boolean
  defaultText?: string
  defaultUrl?: string
  textLabel?: string
  textPlaceholder?: string
}) {
  const [url, setUrl] = useState('')
  const [text, setText] = useState('')
  const [error, setError] = useState('')

  useEffect(() => {
    if (open) {
      setText(defaultText ?? '')
      setUrl(defaultUrl ?? '')
    }
  }, [open, defaultText, defaultUrl])

  const handleSubmit = () => {
    if (!url.trim()) return
    if (!validate(url.trim())) {
      setError(errorMessage)
      return
    }
    onSubmit(url.trim(), showTextField ? text : undefined)
    handleClose()
  }

  const handleClose = () => {
    onOpenChange(false)
    setUrl('')
    setText('')
    setError('')
  }

  return (
    <Dialog.Root
      open={open}
      onOpenChange={v => {
        if (!v) handleClose()
        else onOpenChange(v)
      }}
    >
      <Dialog.Content maxWidth="450px">
        <Dialog.Title>{title}</Dialog.Title>
        <Dialog.Description size="2" mb="4">
          {description}
        </Dialog.Description>
        <Flex direction="column" gap="3">
          <TextField.Root
            placeholder={placeholder}
            value={url}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              setUrl(e.target.value)
              setError('')
            }}
            onKeyDown={(e: React.KeyboardEvent) => {
              if (e.key === 'Enter') handleSubmit()
            }}
          />
          {showTextField && (
            <Flex direction="column" gap="1">
              <Text as="label" size="2" weight="medium">
                {textLabel}
              </Text>
              <TextField.Root
                value={text}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setText(e.target.value)
                }
                placeholder={textPlaceholder}
                onKeyDown={(e: React.KeyboardEvent) => {
                  if (e.key === 'Enter') handleSubmit()
                }}
              />
            </Flex>
          )}
          {error && (
            <Text color="red" size="1">
              {error}
            </Text>
          )}
        </Flex>
        <Flex gap="3" mt="4" justify="end">
          <Dialog.Close>
            <Button variant="soft" color="gray">
              Cancel
            </Button>
          </Dialog.Close>
          <Button variant="solid" onClick={handleSubmit}>
            Insert
          </Button>
        </Flex>
      </Dialog.Content>
    </Dialog.Root>
  )
}
